import React, { useEffect, useMemo, useState } from "react";
import { addInwardPayment, getInwardEntries, getSalesEntries } from "../storage";
import { formatDisplayDate } from "../utils/dateFormat";
import { getSettlementSummary } from "../utils/settlement";
import "./PurchaseSalesDashboard.css";

const moneyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

const PAYMENT_MODES = ["Cash", "UPI", "Cheque", "Acc Transfer", "Card", "Razorpay"];

function formatMoney(value) {
  return moneyFormatter.format(Number(value) || 0);
}

function dateKey(value) {
  return String(value || "").slice(0, 10);
}

function localToday() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function settlementFor(entry) {
  const summary = getSettlementSummary(entry?.total, entry?.payments);
  if ((entry?.paymentDone === true || Number(entry?.paymentDone) === 1) && summary.settledAmount === 0) {
    return { ...summary, paidAmount: summary.totalAmount, settledAmount: summary.totalAmount, pendingAmount: 0 };
  }
  return summary;
}

function totalsFor(entries) {
  return entries.reduce(
    (totals, entry) => {
      const settlement = settlementFor(entry);
      totals.amount += settlement.totalAmount;
      totals.paid += settlement.paidAmount;
      totals.pending += settlement.pendingAmount;
      totals.count += 1;
      return totals;
    },
    { amount: 0, paid: 0, pending: 0, count: 0 }
  );
}

function makePaymentForm(supplierName = "") {
  return {
    supplierName,
    date: localToday(),
    amount: "",
    mode: "Cash",
  };
}

function OverviewPanel({ title, totalLabel, totals, tone }) {
  return (
    <section className={`ps-overview-panel ps-overview-${tone}`}>
      <div className="ps-overview-heading">
        <span className="ps-overview-marker" aria-hidden="true" />
        <h2>{title}</h2>
      </div>
      <div className="ps-overview-total">
        <span>{totalLabel}</span>
        <strong>{formatMoney(totals.amount)}</strong>
      </div>
      <div className="ps-overview-details">
        <div><span>Paid</span><strong>{formatMoney(totals.paid)}</strong></div>
        <div><span>Pending</span><strong>{formatMoney(totals.pending)}</strong></div>
        <div><span>Entries</span><strong>{totals.count}</strong></div>
      </div>
    </section>
  );
}

function RecentTable({ title, rows, type }) {
  const isPurchase = type === "purchase";
  return (
    <section className="ps-recent-panel">
      <div className="ps-panel-heading">
        <h2>{title}</h2>
        <span>{rows.length} recent</span>
      </div>
      <div className="ps-table-wrap">
        <table className="ps-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>{isPurchase ? "Supplier" : "Customer"}</th>
              <th className="ps-number">Amount</th>
              <th className="ps-number">Pending</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((entry, index) => {
              const settlement = settlementFor(entry);
              const party = isPurchase
                ? String(entry?.supplierName || "Unknown supplier")
                : String(entry?.customerName || entry?.customer || "Walk-in").split(/\r?\n/)[0];
              const entryDate = isPurchase ? entry?.createdAt : entry?.saleDate;
              return (
                <tr key={entry?.id || `${type}-${index}`}>
                  <td>{formatDisplayDate(entryDate)}</td>
                  <td>{party}</td>
                  <td className="ps-number">{formatMoney(settlement.totalAmount)}</td>
                  <td className="ps-number">{formatMoney(settlement.pendingAmount)}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={4} className="ps-empty">No {type} entries available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function PurchaseSalesDashboard() {
  const [inwardEntries, setInwardEntries] = useState([]);
  const [salesEntries, setSalesEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState(() => makePaymentForm());

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const [purchases, sales] = await Promise.all([getInwardEntries(), getSalesEntries()]);
      setInwardEntries(Array.isArray(purchases) ? purchases : []);
      setSalesEntries(Array.isArray(sales) ? sales : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const purchases = useMemo(
    () => inwardEntries.filter((entry) => String(entry?.category || "General").trim() === "General"),
    [inwardEntries]
  );
  const sales = useMemo(
    () => salesEntries.filter((entry) => String(entry?.category || "").trim() === "Billing"),
    [salesEntries]
  );

  const supplierBalances = useMemo(() => {
    const grouped = new Map();
    purchases.forEach((entry) => {
      const supplierName = String(entry?.supplierName || "").trim();
      if (!supplierName) return;
      const key = supplierName.toLowerCase();
      const settlement = settlementFor(entry);
      if (!grouped.has(key)) {
        grouped.set(key, { supplierName, pendingAmount: 0, entries: [] });
      }
      const supplier = grouped.get(key);
      supplier.pendingAmount += settlement.pendingAmount;
      supplier.entries.push(entry);
    });
    return Array.from(grouped.values())
      .map((supplier) => ({
        ...supplier,
        pendingAmount: +Number(supplier.pendingAmount || 0).toFixed(2),
      }))
      .sort((a, b) => a.supplierName.localeCompare(b.supplierName, undefined, { sensitivity: "base" }));
  }, [purchases]);

  const selectedSupplierBalance = useMemo(
    () =>
      supplierBalances.find(
        (supplier) => supplier.supplierName.toLowerCase() === String(paymentForm.supplierName || "").toLowerCase()
      ) || null,
    [paymentForm.supplierName, supplierBalances]
  );

  const openPaymentEntry = () => {
    const firstPendingSupplier =
      supplierBalances.find((supplier) => supplier.pendingAmount > 0) || supplierBalances[0] || null;
    setPaymentForm(makePaymentForm(firstPendingSupplier?.supplierName || ""));
    setPaymentOpen(true);
  };

  const closePaymentEntry = () => {
    if (paymentSaving) return;
    setPaymentOpen(false);
    setPaymentForm(makePaymentForm());
  };

  const saveSupplierPayment = async (event) => {
    event.preventDefault();
    if (paymentSaving) return;

    const supplierName = String(paymentForm.supplierName || "").trim();
    const amount = +Number(paymentForm.amount || 0).toFixed(2);
    const date = String(paymentForm.date || "").trim();
    const mode = PAYMENT_MODES.includes(paymentForm.mode) ? paymentForm.mode : "Cash";
    const pendingAmount = Number(selectedSupplierBalance?.pendingAmount || 0);

    if (!supplierName || !selectedSupplierBalance) {
      alert("Select a supplier.");
      return;
    }
    if (!date || date > localToday()) {
      alert("Select a valid payment date.");
      return;
    }
    if (amount <= 0) {
      alert("Enter a valid payment amount.");
      return;
    }
    if (amount > pendingAmount + 0.0001) {
      alert(`Payment cannot exceed the pending amount of ${formatMoney(pendingAmount)}.`);
      return;
    }

    const pendingEntries = selectedSupplierBalance.entries
      .map((entry) => ({ entry, pendingAmount: settlementFor(entry).pendingAmount }))
      .filter((row) => row.pendingAmount > 0)
      .sort((a, b) => {
        const dateCompare = dateKey(a.entry?.createdAt).localeCompare(dateKey(b.entry?.createdAt));
        if (dateCompare !== 0) return dateCompare;
        return String(a.entry?.id || "").localeCompare(String(b.entry?.id || ""));
      });

    let remaining = amount;
    setPaymentSaving(true);
    try {
      for (const row of pendingEntries) {
        if (remaining <= 0) break;
        const appliedAmount = +Math.min(remaining, row.pendingAmount).toFixed(2);
        if (appliedAmount <= 0) continue;
        await addInwardPayment(row.entry.id, {
          amount: appliedAmount,
          date,
          type: "payment",
          mode,
        });
        remaining = +(remaining - appliedAmount).toFixed(2);
      }
      await refresh();
      setPaymentOpen(false);
      setPaymentForm(makePaymentForm());
      alert("Supplier payment saved successfully.");
    } catch (err) {
      console.error(err);
      await refresh();
      alert(err?.message || "Unable to save supplier payment.");
    } finally {
      setPaymentSaving(false);
    }
  };

  const today = localToday();
  const purchaseTotals = useMemo(() => totalsFor(purchases), [purchases]);
  const salesTotals = useMemo(() => totalsFor(sales), [sales]);
  const todayPurchaseTotals = useMemo(
    () => totalsFor(purchases.filter((entry) => dateKey(entry?.createdAt) === today)),
    [purchases, today]
  );
  const todaySalesTotals = useMemo(
    () => totalsFor(sales.filter((entry) => dateKey(entry?.saleDate) === today)),
    [sales, today]
  );
  const recentPurchases = useMemo(
    () => [...purchases].sort((a, b) => dateKey(b?.createdAt).localeCompare(dateKey(a?.createdAt))).slice(0, 5),
    [purchases]
  );
  const recentSales = useMemo(
    () => [...sales].sort((a, b) => dateKey(b?.saleDate).localeCompare(dateKey(a?.saleDate))).slice(0, 5),
    [sales]
  );

  return (
    <div className="ps-dashboard">
      <header className="ps-dashboard-header">
        <div>
          <span className="ps-dashboard-kicker">ARF SEAFOODS</span>
          <h1>Purchase &amp; Sales Dashboard</h1>
          <p>A focused view of material purchases and sales performance.</p>
        </div>
        <div className="ps-dashboard-actions">
          <button type="button" className="ps-payment-button" onClick={openPaymentEntry} disabled={loading}>
            Supplier Payment
          </button>
          <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh Data"}
          </button>
        </div>
      </header>

      {error ? <div className="ps-dashboard-error" role="alert">{error}</div> : null}

      <div className="ps-metric-grid" aria-label="Today's purchase and sales summary">
        <article className="ps-metric-card">
          <span>Today&apos;s Purchases</span>
          <strong>{formatMoney(todayPurchaseTotals.amount)}</strong>
          <small>{todayPurchaseTotals.count} entries</small>
        </article>
        <article className="ps-metric-card ps-metric-sales">
          <span>Today&apos;s Sales</span>
          <strong>{formatMoney(todaySalesTotals.amount)}</strong>
          <small>{todaySalesTotals.count} bills</small>
        </article>
        <article className="ps-metric-card">
          <span>Purchase Pending</span>
          <strong>{formatMoney(purchaseTotals.pending)}</strong>
          <small>Across all purchases</small>
        </article>
        <article className="ps-metric-card ps-metric-sales">
          <span>Sales Pending</span>
          <strong>{formatMoney(salesTotals.pending)}</strong>
          <small>Across all sales</small>
        </article>
      </div>

      <div className="ps-overview-grid">
        <OverviewPanel title="Purchase Overview" totalLabel="Total purchases" totals={purchaseTotals} tone="purchase" />
        <OverviewPanel title="Sales Overview" totalLabel="Total sales" totals={salesTotals} tone="sales" />
      </div>

      <div className="ps-recent-grid">
        <RecentTable title="Recent Purchases" rows={recentPurchases} type="purchase" />
        <RecentTable title="Recent Sales" rows={recentSales} type="sales" />
      </div>

      {paymentOpen ? (
        <div className="ps-payment-backdrop" role="presentation" onMouseDown={closePaymentEntry}>
          <section
            className="ps-payment-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="supplier-payment-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="ps-payment-heading">
              <div>
                <span>PAYMENTS</span>
                <h2 id="supplier-payment-title">Supplier Payment</h2>
                <p>Select a supplier and enter a payment against the pending purchase balance.</p>
              </div>
              <button type="button" className="ps-payment-close" onClick={closePaymentEntry} disabled={paymentSaving}>
                Close
              </button>
            </div>

            <form className="ps-payment-form" onSubmit={saveSupplierPayment}>
              <label className="ps-payment-field ps-payment-field-wide">
                <span>Supplier</span>
                <select
                  value={paymentForm.supplierName}
                  onChange={(event) =>
                    setPaymentForm((current) => ({ ...current, supplierName: event.target.value, amount: "" }))
                  }
                  required
                >
                  <option value="">Select supplier</option>
                  {supplierBalances.map((supplier) => (
                    <option key={supplier.supplierName} value={supplier.supplierName}>
                      {supplier.supplierName}
                    </option>
                  ))}
                </select>
              </label>

              <div className="ps-payment-balance" aria-live="polite">
                <span>Pending Amount</span>
                <strong>{formatMoney(selectedSupplierBalance?.pendingAmount || 0)}</strong>
              </div>

              <label className="ps-payment-field">
                <span>Payment Date</span>
                <input
                  type="date"
                  value={paymentForm.date}
                  max={localToday()}
                  onChange={(event) => setPaymentForm((current) => ({ ...current, date: event.target.value }))}
                  required
                />
              </label>

              <label className="ps-payment-field">
                <span>Payment Mode</span>
                <select
                  value={paymentForm.mode}
                  onChange={(event) => setPaymentForm((current) => ({ ...current, mode: event.target.value }))}
                >
                  {PAYMENT_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}
                </select>
              </label>

              <label className="ps-payment-field ps-payment-field-wide">
                <span>Payment Amount</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={selectedSupplierBalance?.pendingAmount || undefined}
                  value={paymentForm.amount}
                  onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))}
                  placeholder="Enter amount"
                  required
                />
              </label>

              {!supplierBalances.length ? (
                <div className="ps-payment-empty">No material-purchase suppliers are available.</div>
              ) : selectedSupplierBalance && selectedSupplierBalance.pendingAmount <= 0 ? (
                <div className="ps-payment-empty">This supplier has no pending purchase balance.</div>
              ) : null}

              <div className="ps-payment-actions">
                <button type="button" className="btn-secondary" onClick={closePaymentEntry} disabled={paymentSaving}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ps-payment-save"
                  disabled={paymentSaving || !selectedSupplierBalance || selectedSupplierBalance.pendingAmount <= 0}
                >
                  {paymentSaving ? "Saving Payment..." : "Save Payment"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
